// ============================================
// reflectCompute — Slang reflection JSON → ComputeShaderReflection
// ============================================
//
// The compute counterpart of `reflectSlang` (materials). Consumes slangc `-reflection-json` /
// slang-wasm `getLayout().toJsonObject()` for a module prepared by `prepareComputeModule` and
// digests it into the engine's compute reflection model (see types.ts). Key JSON shapes (verified
// by the Phase-0 spike, .refactor/COMPUTE-SHADERS-DESIGN.md §Spike):
//
//  - module-level resources: `{name, binding:{descriptorTableSlot,index}, type:{resource, baseShape,
//    access?, resultType}}`; storage-texture `[format]` lands as a sibling `format` field;
//  - module-level plain uniforms: `{binding:{uniform, offset, size}}` — they live in Slang's auto
//    constant buffer (`globalParams`), which has NO parameter of its own: its slot is parsed from
//    the emitted WGSL (fallback: @group(0) @binding(0));
//  - struct-kernel instances (`rp_kinst_<K>`): a struct param whose `bindings[]` carries the
//    descriptor BASE (+count) and the uniform sub-block base; resource fields hold RELATIVE
//    descriptor indices, data fields hold RELATIVE uniform offsets;
//  - `entryPoints[].threadGroupSize` — cross-checked against the scan (the scan is authoritative,
//    we generated `[numthreads]` from it).
//
// Never throws — malformed reflection yields a partial result with diagnostics.

import type {
  ComputeBindingEntry,
  ComputeDiag,
  ComputeKernelReflection,
  ComputeShaderReflection,
  ComputeStructLayout,
  SlangComputeBinding,
  SlangComputeParam,
  SlangComputeReflectionJson,
  SlangComputeType,
} from './types';
import { computeStructLayout, typeLabelOf, typeLayoutOf } from './wgslLayout';
import { entryPointOf, KERNEL_INSTANCE_PREFIX, type PreparedComputeModule } from './wrapperGen';

// Slang [format("...")] names (GLSL image formats) → WGSL storage texture formats.
const SLANG_FORMAT_TO_WGSL: Record<string, string> = {
  rgba8: 'rgba8unorm',
  rgba8_snorm: 'rgba8snorm',
  rgba8ui: 'rgba8uint',
  rgba8i: 'rgba8sint',
  rgba16f: 'rgba16float',
  rgba16ui: 'rgba16uint',
  rgba16i: 'rgba16sint',
  rgba32f: 'rgba32float',
  rgba32ui: 'rgba32uint',
  rgba32i: 'rgba32sint',
  rg32f: 'rg32float',
  rg32ui: 'rg32uint',
  rg32i: 'rg32sint',
  r32f: 'r32float',
  r32ui: 'r32uint',
  r32i: 'r32sint',
  rgb10_a2: 'rgb10a2unorm',
  r11f_g11f_b10f: 'rg11b10ufloat',
};

// Formats browser WebGPU allows for read_write storage textures.
const RW_SAFE_FORMATS = new Set(['r32float', 'r32uint', 'r32sint']);

/** Default storage format Slang emits for a texel type with no [format] attribute. */
function defaultStorageFormat(resultType: SlangComputeType | undefined): string {
  const scalar = resultType?.kind === 'vector' ? resultType.elementType?.scalarType : resultType?.scalarType;
  const count = resultType?.kind === 'vector' ? (resultType.elementCount ?? 4) : 1;
  const base = scalar === 'uint32' ? 'uint' : scalar === 'int32' ? 'sint' : 'float';
  const bits = scalar === 'float16' ? 16 : 32;
  const prefix = count === 1 ? 'r' : count === 2 ? 'rg' : 'rgba';
  return `${prefix}${bits}${base}`;
}

/** Element stride of a buffer's element type in the storage address space. */
function elementStrideOf(resultType: SlangComputeType | undefined): number {
  const l = typeLayoutOf(resultType, 'storage');
  return Math.ceil(l.size / l.align) * l.align;
}

function normalizeBindings(p: SlangComputeParam): SlangComputeBinding[] {
  return p.bindings ?? (p.binding ? [p.binding] : []);
}

/** Classify a resource type → binding-entry kind, or null for unsupported resources. */
function resourceKind(type: SlangComputeType): ComputeBindingEntry['kind'] | null {
  if (type.baseShape === 'structuredBuffer') return type.access === 'readWrite' ? 'rwbuffer' : 'buffer';
  if (type.baseShape === 'texture2D') {
    return type.access === 'readWrite' || type.access === 'write' ? 'rwtexture' : 'texture';
  }
  return null;
}

/** Build a buffer/texture binding entry (absolute binding slot already resolved by the caller). */
function resourceEntry(
  name: string,
  owner: string,
  type: SlangComputeType,
  binding: number,
  format: string | undefined,
  diags: ComputeDiag[],
  declLine: number,
): ComputeBindingEntry | null {
  const kind = resourceKind(type);
  if (!kind) {
    diags.push({
      severity: 'warning',
      message: `resource '${name}' (${type.baseShape ?? type.kind}) is not supported by engine.gpu v1 — only (RW)StructuredBuffer, Texture2D and (R)WTexture2D bind`,
      line: declLine,
      col: 0,
    });
    return null;
  }
  const entry: ComputeBindingEntry = {
    name,
    owner,
    kind,
    access: type.access === 'readWrite' ? 'readwrite' : type.access === 'write' ? 'write' : 'read',
    type: typeLabelOf(type.resultType),
    group: 0,
    binding,
    size: kind === 'buffer' || kind === 'rwbuffer' ? elementStrideOf(type.resultType) : 0,
  };
  if (kind === 'buffer' || kind === 'rwbuffer') {
    if (type.resultType?.kind === 'struct') entry.layout = computeStructLayout(type.resultType, 'storage');
  } else if (kind === 'rwtexture') {
    entry.storageFormat = (format && SLANG_FORMAT_TO_WGSL[format]) ?? defaultStorageFormat(type.resultType);
    if (type.access === 'readWrite' && !RW_SAFE_FORMATS.has(entry.storageFormat)) {
      diags.push({
        severity: 'warning',
        message: `RWTexture2D '${name}' is ${entry.storageFormat} read_write — browser WebGPU only allows read_write for r32float/r32uint/r32sint. Use write-only WTexture2D + .Store() (optionally with [format("...")]) or RWTexture2D<float>.`,
        line: declLine,
        col: 0,
      });
    }
  }
  return entry;
}

/** Locate the auto constant buffer (`globalParams`) slot in the emitted WGSL. */
function findGlobalParamsSlot(wgsl: string): { group: number; binding: number } {
  const m = /@binding\((\d+)\)\s*@group\((\d+)\)\s*var<uniform>\s*globalParams\w*\s*:/.exec(wgsl);
  if (m) return { binding: Number(m[1]), group: Number(m[2]) };
  return { group: 0, binding: 0 }; // spike rule: the auto cbuffer takes slot 0 when uniforms exist
}

/**
 * Digest a compiled compute module's reflection into the engine model. `prepared` supplies the
 * scanned kernels (names, numthreads, decl lines) and `emittedWgsl` the auto-cbuffer slot.
 */
export function reflectCompute(
  reflection: unknown,
  prepared: PreparedComputeModule,
  emittedWgsl = '',
): ComputeShaderReflection {
  const json = (reflection ?? {}) as SlangComputeReflectionJson;
  const params = json.parameters ?? [];
  const diagnostics: ComputeDiag[] = [];
  const bindings: ComputeBindingEntry[] = [];
  const declLineOf = (kernel: string): number => prepared.kernels.find((k) => k.name === kernel)?.declLine ?? 0;

  for (const p of params) {
    const type = p.type;
    if (!type) continue;

    // Struct-kernel instance (generated `rp_kinst_<K>` global) → the kernel's own bindings.
    if (p.name.startsWith(KERNEL_INSTANCE_PREFIX) && type.kind === 'struct') {
      const kernelName = p.name.slice(KERNEL_INSTANCE_PREFIX.length);
      const line = declLineOf(kernelName);
      const bases = normalizeBindings(p);
      const descriptorBase = bases.find((b) => b.kind === 'descriptorTableSlot')?.index ?? 0;
      const uniformBase = bases.find((b) => b.kind === 'uniform')?.offset ?? 0;
      for (const f of type.fields ?? []) {
        if (f.type.kind === 'resource') {
          const entry = resourceEntry(
            f.name, kernelName, f.type, descriptorBase + (f.binding?.index ?? 0), undefined, diagnostics, line,
          );
          if (entry) bindings.push(entry);
        } else if (f.binding?.kind === 'uniform') {
          bindings.push({
            name: f.name,
            owner: kernelName,
            kind: 'uniform',
            access: 'read',
            type: typeLabelOf(f.type),
            group: 0,
            binding: 0,
            offset: uniformBase + (f.binding.offset ?? 0),
            size: f.binding.size ?? typeLayoutOf(f.type, 'uniform').size,
          });
        }
      }
      continue;
    }

    // Module-level plain uniform (lives in the auto cbuffer).
    if (p.binding?.kind === 'uniform') {
      bindings.push({
        name: p.name,
        owner: '',
        kind: 'uniform',
        access: 'read',
        type: typeLabelOf(type),
        group: 0,
        binding: 0,
        offset: p.binding.offset ?? 0,
        size: p.binding.size ?? typeLayoutOf(type, 'uniform').size,
        ...(type.kind === 'struct' ? { layout: computeStructLayout(type, 'uniform') } : {}),
      });
      continue;
    }

    // Module-level resource.
    if (type.kind === 'resource') {
      const entry = resourceEntry(p.name, '', type, p.binding?.index ?? 0, p.format, diagnostics, 0);
      if (entry) bindings.push(entry);
      continue;
    }

    if (type.kind === 'samplerState') {
      diagnostics.push({
        severity: 'warning',
        message: `sampler '${p.name}' is not supported by engine.gpu v1 — textures are Load/Store only`,
        line: 0,
        col: 0,
      });
      continue;
    }
    if (type.kind === 'constantBuffer') {
      diagnostics.push({
        severity: 'warning',
        message: `explicit ConstantBuffer '${p.name}' is not supported by engine.gpu v1 — declare plain 'uniform' fields instead (they collapse into the module's auto constant buffer)`,
        line: 0,
        col: 0,
      });
    }
  }

  // Uniform block (the auto cbuffer): every uniform entry, module-global offsets.
  const uniformEntries = bindings.filter((b) => b.kind === 'uniform');
  let uniformBlock: ComputeShaderReflection['uniformBlock'];
  if (uniformEntries.length) {
    const slot = findGlobalParamsSlot(emittedWgsl);
    const end = Math.max(...uniformEntries.map((u) => (u.offset ?? 0) + u.size));
    uniformBlock = {
      name: 'globalParams',
      stride: Math.ceil(end / 16) * 16,
      fields: uniformEntries
        .map((u) => ({ name: u.name, type: u.type, offset: u.offset ?? 0, size: u.size }))
        .sort((a, b) => a.offset - b.offset),
      group: slot.group,
      binding: slot.binding,
    };
  }

  // Kernels: own fields + shared module-level params; validate entry points + thread sizes.
  const moduleLevel = bindings.filter((b) => b.owner === '');
  const entryPoints = json.entryPoints ?? [];
  const kernels: ComputeKernelReflection[] = prepared.kernels.map((k) => {
    const own = bindings.filter((b) => b.owner === k.name);
    const ownNames = new Set(own.map((b) => b.name));
    for (const shadowed of moduleLevel.filter((b) => ownNames.has(b.name))) {
      diagnostics.push({
        severity: 'warning',
        message: `kernel '${k.name}': field '${shadowed.name}' shadows the module-level parameter of the same name — setBuffer/setUniform on this kernel addresses the field`,
        line: k.declLine,
        col: 0,
      });
    }
    const entry = entryPointOf(k.name);
    const ep = entryPoints.find((e) => e.name === entry);
    if (entryPoints.length && !ep) {
      diagnostics.push({
        severity: 'error',
        message: `kernel '${k.name}': generated entry point '${entry}' missing from the compiled module`,
        line: k.declLine,
        col: 0,
      });
    } else if (ep?.threadGroupSize && ep.threadGroupSize.some((v, i) => v !== k.numThreads[i])) {
      diagnostics.push({
        severity: 'warning',
        message: `kernel '${k.name}': reflected threadGroupSize [${ep.threadGroupSize}] differs from scanned [${k.numThreads}]`,
        line: k.declLine,
        col: 0,
      });
    }
    return {
      name: k.name,
      entryPoint: entry,
      numThreads: k.numThreads,
      model: k.model,
      bindings: [...own, ...moduleLevel.filter((b) => !ownNames.has(b.name))],
    };
  });

  return { kernels, bindings, uniformBlock, diagnostics };
}
